import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Stack, Chip, Tooltip } from '@mui/material';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Info as InfoIcon } from '@mui/icons-material';

import { breadcrumbsActions } from '@store/breadcrumbs';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../../../components/TableToolbar';

import {
  getDynamicRuleList,
  selectIsLoadingDynamicRuleList,
  selectDynamicRuleList,
} from '../../../store/payment-orchestration-dynamic-rules.slice';

import {
  dynamicRuleListBreadcrumb,
  DYNAMIC_RULE_ADD_ROUTE,
  DYNAMIC_RULE_LIST_ROUTE,
} from '../../../payment-orchestration.breadcrumbs';

import {
  PAGE_DETAILS,
  COLUMN_WIDTH,
  PAYMENT_ORCHESTRATION_DYNAMIC_RULES_COLUMNS,
} from '../../../payment-orchestration.constants';
import {
  getDynamicRuleListAlignment,
  getDynamicRuleListValue,
  getDynamicRuleListFormattedValue,
} from '../../../payment-orchestration.utils';

const DataGrid = styled(MuiDataGrid)``;

function ActionToolbar({ navigate, permissions }) {
  return (
    <TableToolbar>
      <Button
        variant="contained"
        disabled={!permissions?.canCreatePaymentOrchRule}
        onClick={() => navigate(DYNAMIC_RULE_ADD_ROUTE)}
        startIcon={<AddIcon />}>
        Add new dynamic rule
      </Button>
    </TableToolbar>
  );
}

function getConditionsCustomColumn(value) {
  return (
    <Stack
      gap={2}
      p={2}>
      {value.map(({ operator, parameterName, parameterValue, optional }) => (
        <Stack
          key={parameterValue}
          flexDirection="row"
          alignItems="center"
          gap={1}>
          <Chip
            label={`${parameterName} ${operator} ${parameterValue}`}
            variant="outlined"
          />
          {optional && (
            <Tooltip title="This condition is Optional!">
              <InfoIcon
                color="info"
                fontSize="small"
              />
            </Tooltip>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

function getDynamicRuleListCustomColumn(value, field) {
  switch (field) {
    case 'conditions':
      return getConditionsCustomColumn(value);

    default:
      return null;
  }
}

function getRuleListColumns() {
  return PAYMENT_ORCHESTRATION_DYNAMIC_RULES_COLUMNS.map(
    ({ width = COLUMN_WIDTH.MD, ...col }) => ({
      ...col,
      width,
      sortable: false,
      headerAlign: getDynamicRuleListAlignment(col.field),
      align: getDynamicRuleListAlignment(col.field),
      valueGetter: ({ row }) => getDynamicRuleListValue(row, col.field),
      valueFormatter: ({ value }) =>
        getDynamicRuleListFormattedValue(value, col.field),
      renderCell: ({ value }) =>
        getDynamicRuleListCustomColumn(value, col.field),
    }),
  );
}

function DynamicRulesList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dynamicRuleList = useSelector(selectDynamicRuleList);
  const isLoadingDynamicRuleList = useSelector(selectIsLoadingDynamicRuleList);

  useEffect(() => {
    if (!permissions?.canViewPaymentOrchDynamicRule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.setBreadcrumbs(dynamicRuleListBreadcrumb));
  }, [dispatch]);

  // API: Get Dynamic Rule List
  useEffect(() => {
    dispatch(getDynamicRuleList());
  }, [dispatch]);

  return (
    <ApplicationPageTemplate
      title={PAGE_DETAILS.dynamicRules.title}
      description={PAGE_DETAILS.dynamicRules.description}
      pageReady>
      <DataGrid
        getRowHeight={() => 'auto'}
        loading={isLoadingDynamicRuleList}
        density="compact"
        rows={dynamicRuleList}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        components={{
          Toolbar: () =>
            ActionToolbar({
              navigate,
              permissions,
            }),
        }}
        onRowClick={({ row }) => {
          navigate(`${DYNAMIC_RULE_LIST_ROUTE}/${row.id}`);
        }}
        getRowId={({ id }) => id}
        columns={getRuleListColumns()}
      />
    </ApplicationPageTemplate>
  );
}

export default DynamicRulesList;
